import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { apiFetch } from "../api/client.js";
import { sendSocket } from "../api/sync.js";
import { useAppActions } from "../app/AppProvider.tsx";
import { useModal } from "../components/ModalProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import type { EntityId, Lead } from "../types.ts";
import {
  filterLeadsForFunnel,
  getActiveFunnel,
  leadsForStage,
  openChatPatchForLead,
  preferredWhatsappAccount,
} from "./Kanban.helpers.ts";
import { LeadCard } from "./kanban/LeadCard.tsx";
import { LeadDetail } from "./kanban/LeadDetail.tsx";
import { LeadStageContextMenu, type LeadStageContextMenuState } from "./kanban/LeadStageContextMenu.tsx";

export function Kanban() {
  const { state, dispatch, patch } = useAppActions();
  const { showToast } = useToast();
  const modal = useModal();
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [mobileStageId, setMobileStageId] = useState("");
  const [stageContextMenu, setStageContextMenu] = useState<LeadStageContextMenuState | null>(null);

  const funnel = useMemo(
    () => getActiveFunnel(state.funnels, state.activeFunnelId),
    [state.funnels, state.activeFunnelId],
  );
  const filteredLeads = useMemo(
    () => (funnel ? filterLeadsForFunnel(state.leads, funnel.id, state.query || "") : []),
    [funnel, state.leads, state.query],
  );
  const stageLeadsById = useMemo(() => {
    const items = new Map<string, Lead[]>();
    if (!funnel) return items;
    for (const stage of funnel.stages) {
      items.set(stage.id, leadsForStage(filteredLeads, stage.id));
    }
    return items;
  }, [filteredLeads, funnel]);
  const activeMobileStage = funnel?.stages.find((stage) => stage.id === mobileStageId) || funnel?.stages[0] || null;
  const activeMobileStageCount = activeMobileStage ? stageLeadsById.get(activeMobileStage.id)?.length || 0 : 0;
  const selectedLead = state.leads.find((lead) => String(lead.id) === state.selectedLeadId) || null;

  useEffect(() => {
    if (!funnel) {
      if (mobileStageId) setMobileStageId("");
      return;
    }
    if (!funnel.stages.some((stage) => stage.id === mobileStageId)) {
      setMobileStageId(funnel.stages[0]?.id || "");
    }
  }, [funnel, mobileStageId]);

  useEffect(() => {
    if (!stageContextMenu) return;
    const close = () => setStageContextMenu(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const clickTimer = window.setTimeout(() => document.addEventListener("click", close, { once: true }), 0);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.clearTimeout(clickTimer);
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [stageContextMenu]);

  function selectFunnel(funnelId: string) {
    patch({ activeFunnelId: funnelId });
    sendSocket("active_funnel.set", { activeFunnelId: funnelId });
  }

  async function clearFunnelLeads() {
    if (!funnel) return;
    const funnelLeads = state.leads.filter((lead) => lead.funnelId === funnel.id);
    if (!funnelLeads.length) {
      showToast("Nao ha leads para limpar nesse funil", "info");
      return;
    }
    const choice = await modal.open({
      kind: "danger",
      title: `Limpar funil "${funnel.title}"`,
      message: `Isso vai DELETAR PERMANENTEMENTE ${funnelLeads.length} lead${funnelLeads.length > 1 ? "s" : ""} do funil "${funnel.title}". A acao e irreversivel. Continuar?`,
      actions: [
        { label: "Cancelar", value: "cancel", variant: "secondary" },
        { label: `Deletar ${funnelLeads.length} lead${funnelLeads.length > 1 ? "s" : ""}`, value: "ok", variant: "danger" },
      ],
    });
    if (choice !== "ok") return;
    try {
      const result = await apiFetch("/leads/bulk-clear", {
        method: "POST",
        body: JSON.stringify({ funnelId: funnel.id }),
      });
      const ids = new Set((result as { ids?: string[] })?.ids || []);
      if (ids.size) {
        const remaining = state.leads.filter((lead) => !ids.has(String(lead.id)));
        patch({ leads: remaining });
      }
      showToast(`${(result as { deleted?: number })?.deleted ?? funnelLeads.length} lead(s) removidos`, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao limpar funil", "error");
    }
  }

  function persistLeadChange(lead: Lead, changes: Partial<Lead>) {
    const next: Lead = { ...lead, ...changes, updatedAt: new Date().toISOString() };
    dispatch({ type: "UPSERT_LEAD", lead: next });
    if (typeof changes.stage === "string" && changes.stage) {
      setMobileStageId(changes.stage);
    }
    if (!sendSocket("lead.upsert", next)) {
      apiFetch("/leads/upsert", { method: "POST", body: JSON.stringify(next) }).catch(() => undefined);
    }
  }

  function moveLead(leadId: EntityId, stageId: string) {
    const lead = state.leads.find((item) => String(item.id) === String(leadId));
    if (!lead || lead.stage === stageId) return;
    persistLeadChange(lead, { stage: stageId });
  }

  function openChatForLead(lead: Lead) {
    const result = openChatPatchForLead(
      lead,
      state.chats,
      preferredWhatsappAccount(state.accounts || [], state.accountFilter || [], state.whatsappSession),
    );
    if (result.error || !result.patch) {
      showToast(result.error || "Nao foi possivel iniciar a conversa desse lead", "error");
      return;
    }
    patch(result.patch);
  }

  function scheduleCadenceForLead(lead: Lead) {
    patch({
      panel: "calendar",
      cadenceDraftLeadId: String(lead.id),
      cadenceSchedulerOpen: true,
    });
  }

  async function deleteLead(lead: Lead) {
    const choice = await modal.open({
      kind: "danger",
      title: "Excluir lead",
      message: `Excluir lead ${lead.name || "Sem nome"}? Esta acao nao pode ser desfeita pela interface.`,
      destructive: true,
      actions: [
        { label: "Cancelar", value: "cancel", variant: "secondary" },
        { label: "Excluir", value: "delete", variant: "danger" },
      ],
    });
    if (choice !== "delete") return;

    try {
      await apiFetch(`/leads/${encodeURIComponent(String(lead.id))}`, { method: "DELETE" });
      dispatch({ type: "DELETE_LEAD", id: lead.id });
      showToast("Lead excluido", "info");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao excluir", "error");
    }
  }

  return (
    <div className="kanban-pane">
      <div className="funnel-tabs">
        {state.funnels.map((item) => (
          <button
            key={item.id}
            className={`funnel-tab ${state.activeFunnelId === item.id ? "is-active" : ""}`}
            type="button"
            onClick={() => selectFunnel(item.id)}
          >
            {item.title}
          </button>
        ))}
        {funnel ? (
          <button
            className="funnel-clear"
            type="button"
            title={`Deletar todos os leads do funil "${funnel.title}"`}
            onClick={() => void clearFunnelLeads()}
          >
            Limpar funil
          </button>
        ) : null}
      </div>

      {!funnel ? (
        <div className="kanban-board kanban-empty">Sem funis configurados.</div>
      ) : (
        <>
          <div className="kanban-mobile-toolbar">
            <div className="kanban-mobile-overview">
              <div className="kanban-mobile-title">
                <span>{funnel.title}</span>
                <strong>{activeMobileStage?.title || "Sem etapa"}</strong>
              </div>
              <div className="kanban-mobile-count">
                <strong>{activeMobileStageCount}</strong>
                <span>{activeMobileStageCount === 1 ? "lead" : "leads"}</span>
              </div>
            </div>
            <div className="kanban-stage-tabs" role="tablist" aria-label="Etapas do funil">
              {funnel.stages.map((stage) => {
                const stageLeads = stageLeadsById.get(stage.id) || [];
                const isActive = activeMobileStage?.id === stage.id;
                return (
                  <button
                    key={stage.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`kanban-stage-tab ${isActive ? "is-active" : ""}`}
                    onClick={() => setMobileStageId(stage.id)}
                  >
                    <span className="stage-color" style={{ background: stage.color }} />
                    <span className="kanban-stage-label">{stage.title}</span>
                    <span className="kanban-stage-count">{stageLeads.length}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="kanban-board">
            {funnel.stages.map((stage) => {
              const stageLeads = stageLeadsById.get(stage.id) || [];
              const isMobileActive = activeMobileStage?.id === stage.id;
              return (
                <section
                  key={stage.id}
                  className={`kanban-col stage-${stage.id} ${dragOverStageId === stage.id ? "drag-over" : ""} ${isMobileActive ? "is-mobile-active" : ""}`}
                  data-stage={stage.id}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOverStageId(stage.id);
                  }}
                  onDragLeave={() => setDragOverStageId(null)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragOverStageId(null);
                    const leadId = event.dataTransfer.getData("text/x-lead-id");
                    if (leadId) moveLead(leadId, stage.id);
                  }}
                >
                  <div className="stage-head" style={{ "--stage-color": stage.color } as CSSProperties}>
                    <span className="stage-color" style={{ background: stage.color }} />
                    <span className="stage-title">{stage.title}</span>
                    <span className="stage-count">{stageLeads.length}</span>
                  </div>
                  <div className="stage-body">
                    {stageLeads.length ? (
                      stageLeads.map((lead) => (
                        <LeadCard
                          key={String(lead.id)}
                          lead={lead}
                          selected={state.selectedLeadId === String(lead.id)}
                          onSelect={() => patch({ selectedLeadId: String(lead.id) })}
                          onContextMenu={(event) => {
                            event.preventDefault();
                            patch({ selectedLeadId: String(lead.id) });
                            setStageContextMenu({ lead, x: event.clientX, y: event.clientY });
                          }}
                        />
                      ))
                    ) : (
                      <div className="kanban-stage-empty">Sem leads nesta etapa.</div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      {selectedLead ? (
        <LeadDetail
          key={String(selectedLead.id)}
          lead={selectedLead}
          stages={state.funnels.find((item) => item.id === selectedLead.funnelId)?.stages || []}
          onClose={() => patch({ selectedLeadId: "" })}
          onOpenChat={() => openChatForLead(selectedLead)}
          onScheduleCadence={() => scheduleCadenceForLead(selectedLead)}
          onDelete={() => void deleteLead(selectedLead)}
          onCommit={(changes) => persistLeadChange(selectedLead, changes)}
        />
      ) : null}

      {stageContextMenu && funnel ? (
        <LeadStageContextMenu
          menu={stageContextMenu}
          stages={funnel.stages}
          onMove={moveLead}
          onClose={() => setStageContextMenu(null)}
        />
      ) : null}
    </div>
  );
}
